/* eslint-disable max-statements */
import snakecase from 'lodash.snakecase';

import {RESOURCES} from '~constants/config';
import {snakecaseObject} from '~utils/snakecaseObject';
import {Document, Image, Link} from '~components/GalleryItemsInput/classes';

export const parseResponse = rawResponse => ({
    data: rawResponse.data.page,
    total: rawResponse.data.count
});

// A relation arrives nested ({id, name, ...}) on an Accion and as a bare id on a draft.
const fkId = value => (value && typeof value === 'object' ? value.id : value);
const fkIds = values => (Array.isArray(values) ? values.map(fkId) : []);
// Either key spelling: API serializer names, or model names stored in a draft's `children`.
const pick = (obj, apiKey, modelKey) => (obj[apiKey] !== undefined ? obj[apiKey] : obj[modelKey]);

// No amount means "sin identificar", a checkbox in the form.
const investmentFields = investment => ({
    estimated_investment: investment,
    investment_unidentified: investment === null || investment === undefined
});

// Map an Accion (or draft) read payload, already snakecased, onto the form's flat ids.
const parseAccionFields = (d, dynamic_center, static_center) => ({
    ...d,
    is_portfolio: !!d.is_portfolio,
    is_bid: !!d.is_bid,
    is_caf: !!d.is_caf,
    is_fonplata: !!d.is_fonplata,
    accompaniment: !!d.accompaniment,
    is_anchor: !!d.is_anchor,
    countries: fkIds(d.countries),
    territories: fkIds(d.territories),
    subthemes: fkIds(d.subthemes),
    types: fkIds(d.types),
    scopes: fkIds(d.scopes),
    // Edited as {name} rows: SimpleFormIterator seeds new rows with an object.
    responsibles: (d.responsibles || []).map(name => ({name})),
    stage: fkId(d.stage),
    term: fkId(d.term),
    source: fkId(d.source),
    studies_funder: fkId(d.studies_funder),
    prioritization: fkId(d.prioritization),
    // An Accion reads its amount as `total_investment`, a draft as `estimated_investment`.
    ...investmentFields(pick(d, 'estimated_investment', 'total_investment')),
    funding: (d.funding || []).map(e => ({
        funder: fkId(pick(e, 'funder', 'financiador_estudios')),
        funding_type: fkId(pick(e, 'funding_type', 'tipo_financiamiento'))
    })),
    segments: (d.segments || []).map(e => ({
        segment: pick(e, 'segment', 'nombre'),
        description: pick(e, 'description', 'descripcion'),
        investment: pick(e, 'investment', 'inversion'),
        stage: fkId(pick(e, 'stage', 'etapa'))
    })),
    dynamic_center_latitude: dynamic_center ? pick(dynamic_center, 'latitude', 'latitud') : null,
    dynamic_center_longitude: dynamic_center ? pick(dynamic_center, 'longitude', 'longitud') : null,
    dynamic_map_zoom: dynamic_center ? dynamic_center.zoom : null,
    static_center_latitude: static_center ? pick(static_center, 'latitude', 'latitud') : null,
    static_center_longitude: static_center ? pick(static_center, 'longitude', 'longitud') : null,
    static_map_zoom: static_center ? static_center.zoom : null
});

export const parseAccion = data => {
    const d = snakecaseObject(data);
    return {...parseAccionFields(d, getDynamicCenter(d), getStaticCenter(d)), id: d.code};
};

// Drafts may be incomplete and keep their relations as ids/dicts in `children`,
// surfaced flat by the draft read serializer; id = uuid.
export const parseAccionBorrador = data => {
    const d = snakecaseObject(data);
    return {...parseAccionFields(d, d.dynamic_center || null, d.static_center || null), id: d.uuid};
};

// List response: drafts already carry flat fields; just key by uuid.
export const formatAccionBorradorResponse = data =>
    data.map(item => ({...item, id: item.uuid || item.code}));

const getDynamicCenter = data => {
    if (data.kml) {
        return data.dynamic_center ? data.dynamic_center : data.dynamicCenter;
    }
    return null;
};

const getStaticCenter = data => {
    if (data.kml_simplified) {
        return data.static_center ? data.static_center : data.staticCenter;
    }
    return null;
};

export const formatCreateTerritorio = data => data;

// eslint-disable-next-line complexity
export const formatAccionFormData = data => {
    let links = [];
    let galleryItems = [];
    if (data.gallery_items) {
        galleryItems = formatGalleryItems(data.gallery_items);
        links = getLinks(galleryItems);
    }
    const request = {
        is_portfolio: !!data.is_portfolio,
        is_bid: !!data.is_bid,
        is_caf: !!data.is_caf,
        is_fonplata: !!data.is_fonplata,
        accompaniment: !!data.accompaniment,
        is_anchor: !!data.is_anchor,
        code: data.code,
        name: data.name,
        goal: data.goal,
        description: data.description,
        stage: data.stage,
        countries: data.countries,
        territories: data.territories,
        subthemes: data.subthemes,
        types: data.types,
        scopes: data.scopes,
        responsibles: (data.responsibles || []).map(responsible => responsible && responsible.name),
        studies_funding_comments: data.studies_funding_comments,
        studies_funder: data.studies_funder,
        kml: data.kml?.rawFile ? data.kml.rawFile : null,
        kml_simplified: data.kml_simplified?.rawFile ? data.kml_simplified.rawFile : null,
        kml_name: data.kml?.rawFile ? data.kml.rawFile.name : data.kml,
        kml_simplified_name: data.kml_simplified?.rawFile ? data.kml_simplified.rawFile.name : data.kml_simplified,
        dynamic_center: data.kml ? {
            latitude: data.dynamic_center_latitude,
            longitude: data.dynamic_center_longitude,
            zoom: data.dynamic_map_zoom
        } : null,
        static_center: data.kml_simplified ? {
            latitude: data.static_center_latitude,
            longitude: data.static_center_longitude,
            zoom: data.static_map_zoom
        } : null,
        segments: data.segments,
        funding: data.funding,
        // An empty multipart value clears it: "sin identificar".
        estimated_investment: data.investment_unidentified ? '' : data.estimated_investment,
        links: JSON.stringify(links),
        gallery_items: galleryItems,
        end_date: data.end_date ? `${data.end_date}-01` : data.end_date
    };
    for (const key in request) {
        if (request[key] === null || request[key] === undefined) {
            delete request[key];
        }
    }
    // Nullable FKs: an empty multipart value clears them; an absent key would keep the old one.
    ['prioritization', 'term', 'source'].forEach(key => {
        request[key] = data[key] === null || data[key] === undefined ? '' : data[key];
    });
    return request;
};

export const formatTerritorioFormData = data => {
    return {
        ...data,
        kmz: data.kmz?.rawFile ? data.kmz.rawFile : undefined,
        ajuste_mapa: data.kmz ? {
            latitude: data.dynamic_center_latitude,
            longitude: data.dynamic_center_longitude,
            zoom: data.dynamic_map_zoom
        } : null,
    };
};

export const formatLayerFormData = data => ({
    name: data.name,
    files: (data.files || []).map(f => f.rawFile).filter(Boolean),
});

const formatGalleryItems = galleryItems =>
    galleryItems.map(gi => {
        let newItem = null;
        switch (gi.type) {
            case 'Document':
                newItem = new Document();
                newItem.populate(gi);
                if (typeof gi.file === 'string') {
                    newItem.file = null;
                }
                break;
            case 'Image':
                newItem = new Image();
                newItem.populate(gi);
                if (typeof gi.file === 'string') {
                    newItem.file = null;
                }
                break;
            case 'Link':
                newItem = new Link();
                newItem.populate(gi);
                break;
            default:
                newItem = {...gi};
                break;
        }
        return newItem;
    });

const getLinks = galleryItems => {
    const links = new Array();
    galleryItems.forEach(gi => {
        if (gi instanceof Link) {
            links.push(gi);
        }
    });
    return links;
};

export const formatAccionResponse = data =>
    data.map(item => ({...item, id: item.code}));


export const formatTerritorioResponse = data => data.map(item => {
    return {
        ...item,
        dynamic_center_latitude: item.ajusteMapa?.latitude || 1,
        dynamic_center_longitude: item.ajusteMapa?.longitude || 1,
        dynamic_map_zoom: item.ajusteMapa?.zoom || 1
    }
});

export const formatShowAccion = data => {
    const parsedData = {
        is_portfolio: !!data.isPortfolio,
        is_bid: !!data.isBid,
        is_caf: !!data.isCaf,
        is_fonplata: !!data.isFonplata,
        accompaniment: !!data.accompaniment,
        is_anchor: !!data.isAnchor,
    };
    for (const key in data) {
        parsedData[snakecase(key)] = data[key];
    }
    parsedData.id = parsedData.code;
    // Expose camelCase alias that ShowCustomActions reads directly.
    // The cerealizr deserializer is skipped for accion detail responses (kml_simplified guard),
    // so the raw API key is snake_case. Map it explicitly here.
    parsedData.publicationStatus = parsedData.publication_status;
    if (parsedData.end_date) {
        parsedData.end_date = parsedData.end_date.substring(0, 7);
    }
    return parsedData;
};

export const parseResourceDetail = {
    actions: parseAccion,
    'draft-actions': parseAccionBorrador,
    territories: item => formatTerritorioResponse([item])[0],
    'metric-values': item => ({
        ...item,
        metric: item.metric ? item.metric.id : null,
        pais: item.pais ? item.pais.id : null,
        etapa: item.etapa ? item.etapa.id : null,
        subtema: item.subtema ? item.subtema.id : null,
    }),
};

// Flat translated catalogs: send the ES name plus the optional EN/PT translations.
const formatCatalog = data => ({ name: data.name, nameEn: data.nameEn || '', namePt: data.namePt || '' });

export const formatCreateResource = {
    territories: formatCreateTerritorio,
    priorizaciones: formatCatalog,
    etapas: data => (data.order != null ? { ...formatCatalog(data), order: data.order } : formatCatalog(data)),
    scopes: formatCatalog,
    terms: formatCatalog,
    sources: data => ({
        ...formatCatalog(data),
        shortName: data.shortName || '',
        shortNameEn: data.shortNameEn || '',
        shortNamePt: data.shortNamePt || '',
        order: data.order || 0,
    }),
    types: formatCatalog,
    themes: formatCatalog,
    subthemes: data => ({ ...formatCatalog(data), theme: data.theme }),
    'chart-categories': formatCatalog,
    studies: data => ({
        titleEs: data.titleEs,
        titleEn: data.titleEn || '',
        titlePt: data.titlePt || '',
        urlEs: data.urlEs,
        urlEn: data.urlEn || '',
        urlPt: data.urlPt || ''
    }),
    'subtheme-categories': data => ({ source: data.source, subtheme: data.subtheme, category: data.category }),
    'financiadores-estudios': formatCatalog,
    paises: data => ({ name: data.name }),
    [RESOURCES.metrics]: data => ({ name: data.name, unit: data.unit, category: data.category }),
    [RESOURCES['metric-values']]: data => ({
        metric: data.metric,
        pais: data.pais || null,
        etapa: data.etapa || null,
        subtema: data.subtema || null,
        value: data.value,
        detalle: data.detalle || ''
    }),
    [RESOURCES.disclaimers]: data => {
        const result = {
            titleEs: data.titleEs,
            disclaimerEs: data.disclaimerEs,
            titleEn: data.titleEn || '',
            disclaimerEn: data.disclaimerEn || '',
            titlePt: data.titlePt || '',
            disclaimerPt: data.disclaimerPt || ''
        };
        if (data.order != null) result.order = data.order;
        return result;
    }
};

const identity = data => data;

export const formatResponseData = {
    actions: formatAccionResponse,
    'draft-actions': formatAccionBorradorResponse,
    territories: formatTerritorioResponse,
    priorizaciones: identity,
    disclaimers: identity,
    studies: identity,
    etapas: identity,
    scopes: identity,
    terms: identity,
    sources: identity,
    'financiadores-estudios': identity,
    types: identity,
    themes: identity,
    subthemes: identity,
    'chart-categories': identity,
    'subtheme-categories': identity,
    paises: identity,
    metrics: identity,
    'metric-values': identity,
    countries: identity,
    layers: identity
};
